import { Alert, Autocomplete, Box, InputAdornment, List, Popper, TextField, Tooltip } from "@mui/material";
import { SyntheticEvent, useCallback, useMemo } from "react";

import DriverAvatar from "@/components/DriverAvatar";
import useDriversContext from "@/providers/data/DriversContext";
import { DriverType } from "@/types/driver.types";

interface DriverFilterProps<T> {
    allRows: T[];
    currentDrivers: string[];
    onDriverChange: (driverIds: string[]) => void;
    disabled?: boolean;
    getDriverId: (row: T) => string;
    driverKey: "id" | "userId";
}

function DriverFilter<T>({
    allRows,
    currentDrivers,
    onDriverChange,
    disabled = false,
    getDriverId,
    driverKey,
}: DriverFilterProps<T>) {
    const { drivers } = useDriversContext();

    const selectedDrivers = useMemo(
        () => drivers.filter((driver) => currentDrivers.includes(driver[driverKey])),
        [currentDrivers, drivers, driverKey]
    );

    const driverTasks = useMemo<Record<string, number>>(
        () =>
            allRows.reduce(
                (prev, curr) => {
                    const driverId = getDriverId(curr);
                    if (!driverId) return prev;
                    let current = prev[driverId];
                    if (current) {
                        prev[driverId] += +1;
                    } else prev[driverId] = 1;

                    return prev;
                },
                {} as Record<string, number>
            ),
        [allRows, getDriverId]
    );

    const handleDriverChange = useCallback(
        (_: SyntheticEvent<Element, Event>, selectedDrivers: DriverType[]) => {
            const driverIds = selectedDrivers.map((driver) => driver[driverKey]);
            onDriverChange(driverIds);
        },
        [onDriverChange, driverKey]
    );

    const CustomPopper = useCallback((props: React.ComponentProps<typeof Popper>) => {
        const { children, ...other } = props;
        return (
            <Popper {...other} placement="bottom-start">
                <div>
                    <List>{typeof children === "function" ? children({ placement: "bottom-start" }) : children}</List>
                    <Alert severity="info" color="info">
                        Filtrelemek istediğiniz kaptanları seçiniz.
                    </Alert>
                </div>
            </Popper>
        );
    }, []);

    return (
        <Autocomplete
            disableCloseOnSelect
            PopperComponent={CustomPopper}
            classes={{
                inputRoot: " !h-[36.7px] -translate-y-[1px] !min-w-[300px] !bg-white",
            }}
            sx={() => ({
                "& .MuiInputBase-root": {
                    paddingBottom: "6.75px",
                    paddingTop: "6.75px",
                },
            })}
            value={selectedDrivers}
            multiple
            options={drivers}
            disabled={disabled}
            getOptionLabel={(option) => option.name}
            onChange={handleDriverChange}
            renderOption={(props, { name, isDeleted, [driverKey]: id }) => {
                const { key, ...optionProps } = props;

                return (
                    <Box
                        key={key}
                        component="li"
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        {...optionProps}
                    >
                        <DriverAvatar avatar name={name} isDeleted={isDeleted}>
                            <Tooltip title="Şoföre Atanmış yolcu sayısı">
                                <span className=" ml-auto grow-0 font-semibold text-gray-500 shrink-0 text-right">
                                    {driverTasks[id]}
                                </span>
                            </Tooltip>
                        </DriverAvatar>
                    </Box>
                );
            }}
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        placeholder={"Kaptanları Filtrele..."}
                        variant="filled"
                        sx={{
                            "& .MuiInputBase-input": {
                                transform: "translateY(-4px)",
                            },
                        }}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <Tooltip title="Filtrelenen kaptan sayısı">
                                    <InputAdornment
                                        position="end"
                                        className="    cursor-help !-translate-x-[6px] !font-semibold !-translate-y-[6px] !w-12 !h-full !flex !items-center !bg-gray-100 !justify-center"
                                    >
                                        {currentDrivers.length}
                                    </InputAdornment>
                                </Tooltip>
                            ),
                        }}
                    />
                );
            }}
        />
    );
}

export default DriverFilter;
